import {
    Avatar,
    AvatarGroup,
    ChatContainer,
    ConversationHeader,
    InputToolbox,
    Message,
    MessageGroup,
    MessageInput,
    MessageList,
    MessageSeparator,
    SendButton,
} from '@chatscope/chat-ui-kit-react'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import moment from 'moment'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import Lightbox from 'react-image-lightbox'
import 'react-image-lightbox/style.css'
import {useAppConfig} from '../../context/app/AppContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import useWindowSize from '../../utils/useWindowSize'
import {useWebsocket} from '../../websocket/WebsocketContext'
import {useAuth} from '../authentication/AuthProvider'
import ImageUploader from '../image/ImageUploader'
import FlexBox from '../layout/FlexBox'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {getChatPageGenericInfo} from './ChatApi'
import {useChatContext} from './ChatProvider'
import {onSenderRoomMessageChange} from './ChatService'
const CHAT_PATH = 'message'
const QueryKey = 'ChatMessages'

const Chat = ({roomData, roomId, onShowChatList = () => {}}) => {
    const {user} = useAuth()

    const {sendChatMessage, readChatMessage} = useWebsocket()
    const {activeParticipantId} = useChatContext()
    const {timezoneId} = useAppConfig()
    const queryClient = useQueryClient()
    const pageRows = 20
    const [pageNumber, setPageNumber] = useState(0)
    const [message, setMessage] = useState('')
    const chatRef = useRef(false)
    const [visible, setVisible] = useState(false)
    const imageRef = useRef(null)

    const [images, setImages] = useState([])
    const [activeIndex, setActiveIndex] = useState(0)
    const [visibleLightBox, setVisibleLightBox] = useState(false)

    useEffect(() => {
        setPageNumber(0)
    }, [roomId])

    const {translateCommons} = TranslateUtils()
    const handleGetPageMessages = async () => {
        const pageData = await getChatPageGenericInfo(
            CHAT_PATH,
            user?.id,
            0,
            pageRows,
            'id',
            -1,
            {roomId: roomId},
        )
        if (!!pageData?.content) {
            let tmp = [...pageData.content]
            tmp.sort((a, b) => a.id - b.id)

            return {
                ...pageData,
                content: tmp,
            }
        }
    }

    const {
        data: messages,
        isLoading,
        isFetching,
    } = useQuery([QueryKey, user?.id, roomId], () => handleGetPageMessages(), {
        enabled: !!user?.id && !!roomId,
    })

    const {mutate: fetchMoreMessages, isLoading: isLoadingFetchMore} =
        useMutation(
            async () =>
                await getChatPageGenericInfo(
                    CHAT_PATH,
                    user?.id,
                    pageNumber + 1,
                    pageRows,
                    'id',
                    -1,
                    {roomId: roomId},
                ),
            {
                onSuccess: data => {
                    if (data && data.content) {
                        queryClient.setQueryData(
                            [QueryKey, user?.id, roomId],
                            oldData => {
                                let tmp = [...data.content]
                                tmp.sort((a, b) => a.id - b.id)

                                return {
                                    ...data,
                                    content: oldData
                                        ? [...tmp, ...oldData.content]
                                        : [...tmp],
                                }
                            },
                        )

                        setPageNumber(data.pageable.pageNumber)
                    }
                },
            },
        )

    const handleFetchMore = () => {
        if (!messages?.last && !isLoadingFetchMore && !isLoading) {
            fetchMoreMessages()
        }
    }

    const messageType = {
        text: 'text',
        image: 'image',
        separator: 'separator',
        name: 'name',
    }

    const handleSendChatMessage = (type = messageType.text, images = []) => {
        if (type === messageType.text && !message) {
            return
        }
        if (!!activeParticipantId) {
            let payload = {
                senderId: activeParticipantId,
                roomId: roomId,
                data: message,
                messageType: type,
                images: images,
                participantId: roomData?.participantId,
            }

            onSenderRoomMessageChange()
            setMessage('')

            if (!!sendChatMessage) {
                sendChatMessage(payload)
            }
        }
    }

    const width = useWindowSize()

    const onSubmitImg = () => {
        if (imageRef.current?.value && imageRef.current?.value.length > 0) {
            handleSendChatMessage(messageType.image, imageRef.current?.value)
            setVisible(false)
        }
    }

    const onNextLightBox = useCallback(() => {
        if (activeIndex === images.length - 1) {
            setActiveIndex(0)
        } else {
            setActiveIndex(activeIndex + 1)
        }
    }, [activeIndex, images.length])

    const onPrevLightBox = useCallback(() => {
        if (activeIndex > images.length - 1) {
            setActiveIndex(0)
        } else {
            setActiveIndex(activeIndex - 1)
        }
    }, [activeIndex, images.length])

    const messageDataList = useMemo(() => {
        const messagesData =
            !!messages && messages?.content && messages.content.length > 0
                ? [...messages.content]
                : []

        let holderList = []

        if (!!isArrayAndNotEmpty(messagesData)) {
            let groupByDateList = []
            let dateGroup

            for (let i = 0; i < messagesData.length; i++) {
                // let messDate = moment(messagesData[i].createdAt).format(
                //     'YYYY-MM-DD',
                // )
                // if (messDate !== dateGroup) {
                //     groupByDateList.push({
                //         messageType: messageType.separator,
                //         data: messagesData[i].createdAt,
                //     })
                //     dateGroup = messDate
                // }
                groupByDateList.push(messagesData[i])
            }
            let holderGroupMessageList = []
            let holderGroupData = {}
            for (let i = 0; i < groupByDateList.length; i++) {
                if (groupByDateList[i].messageType === messageType.separator) {
                    if (holderGroupData && holderGroupMessageList.length > 0) {
                        let item = {
                            ...holderGroupData,
                            messages: [...holderGroupMessageList],
                        }
                        holderList.push(item)
                        holderGroupMessageList = []
                    }
                    holderList.push(groupByDateList[i])
                } else if (i === groupByDateList.length - 1) {
                    let item = {}
                    if (holderGroupData.userId === groupByDateList[i].userId) {
                        item = {
                            ...holderGroupData,
                            messages: [
                                ...holderGroupMessageList,
                                groupByDateList[i],
                            ],
                        }
                    } else {
                        if (holderGroupMessageList.length > 0) {
                            holderList.push({
                                ...holderGroupData,
                                messages: [...holderGroupMessageList],
                            })
                        }
                        item = {
                            ...groupByDateList[i],
                            messages: [groupByDateList[i]],
                        }
                    }

                    holderList.push(item)
                } else if (
                    holderGroupData.userId !== groupByDateList[i].userId
                ) {
                    if (isArrayAndNotEmpty(holderGroupMessageList)) {
                        let item = {
                            ...holderGroupData,
                            messages: [...holderGroupMessageList],
                        }
                        holderList.push(item)
                        holderGroupMessageList = []
                    }
                    holderGroupData = groupByDateList[i]
                    holderGroupMessageList.push(groupByDateList[i])
                } else {
                    holderGroupMessageList.push(groupByDateList[i])
                }
            }
            // for (let i = 0; i < groupByDateList.length; i++) {
            //     if (groupByDateList[i].messageType === messageType.separator) {
            //         holderList.push(groupByDateList[i])
            //         if (holderGroupData && holderGroupMessageList.length > 0) {
            //             let item = {
            //                 ...holderGroupData,
            //                 messages: [...holderGroupMessageList],
            //             }
            //             holderList.push(item)
            //             holderGroupMessageList = []
            //         }
            //     } else {
            //         if (holderUserId !== groupByDateList[i].userId) {
            //             if (
            //                 holderGroupData &&
            //                 holderGroupMessageList.length > 0
            //             ) {
            //                 let item = {
            //                     ...holderGroupData,
            //                     messages: [...holderGroupMessageList],
            //                 }
            //                 holderList.push(item)
            //                 holderGroupMessageList = []
            //                 holderGroupData = groupByDateList[i]
            //             }
            //             holderGroupMessageList.push(groupByDateList[i])
            //             holderUserId = groupByDateList[i].userId
            //         } else {
            //             if (groupByDateList.length - 1 === i) {
            //                 let item = {
            //                     ...holderGroupData,
            //                     messages: [
            //                         ...holderGroupMessageList,
            //                         {...groupByDateList[i]},
            //                     ],
            //                 }
            //                 holderList.push(item)
            //             } else {
            //                 holderGroupMessageList.push(groupByDateList[i])
            //             }
            //         }
            //     }
            // }
        }

        return holderList
    }, [messageType.separator, messages])

    console.log(messageDataList)
    const messageGroupByDate = useMemo(() => {
        const messagesData =
            !!messages && messages?.content && messages.content.length > 0
                ? [...messages.content]
                : []

        let messageGroupByData = {}

        if (messagesData?.length > 0) {
            const listByDate = []
            let position = 0
            let dateGroup
            for (let i = 0; i < messagesData.length; i++) {
                let item = {...messagesData[i]}
                //gr Date
                let messDate = moment(messagesData[i].createdAt).format(
                    'YYYY-MM-DD',
                )
                if (messDate !== dateGroup) {
                    listByDate.push({
                        messageType: 'separator',
                        data: messagesData[i].createdAt,
                    })
                    dateGroup = messDate
                }
                //position
                if (position === 0) {
                    if (
                        messagesData[i]?.userId === messagesData[i + 1]?.userId
                    ) {
                        item.position = 1
                        position = 1
                    } else {
                        item.position = 0
                    }
                } else if (position === 1) {
                    if (
                        messagesData[i]?.userId === messagesData[i + 1]?.userId
                    ) {
                        item.position = 2
                    } else {
                        item.position = 3
                        position = 0
                    }
                }

                item.isUseAvatar =
                    [0, 3].includes(item.position) &&
                    messagesData[i].userId !== user?.id
                if (item.position === 0) {
                    listByDate.push({
                        type: messageType.name,
                        name: item.fullName,
                    })
                }
                listByDate.push(item)
            }
            messageGroupByData = listByDate
        }

        return messageGroupByData
    }, [messageType.name, messages, user?.id])

    const renderAvatarGroup = (avatars, name, max = 1) => {
        return (
            <AvatarGroup className="flex-nowrap" size="md">
                {avatars.map(
                    (avatar, idx) =>
                        idx < 2 && (
                            <Avatar
                                key={idx}
                                src={
                                    avatar ||
                                    'https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_960_720.png'
                                }
                                style={
                                    idx === 1
                                        ? {
                                              marginLeft:
                                                  avatars.length > 1
                                                      ? '-25px'
                                                      : '20px',
                                              //   marginTop: '-20px',
                                          }
                                        : {
                                              marginLeft: '-25px',
                                              //   marginTop: '-10px',
                                          }
                                }
                            />
                        ),
                )}
            </AvatarGroup>
        )
    }
    return (
        <ChatContainer>
            <ConversationHeader>
                {renderAvatarGroup(roomData.avatars)}

                <ConversationHeader.Content userName={roomData?.name} />
                {width <= 770 && (
                    <ConversationHeader.Actions>
                        <FlexBox className={'justify-content-between'}>
                            <Button
                                ref={chatRef}
                                style={{
                                    backgroundColor: '#6ea9d7',
                                }}
                                icon="pi pi-arrow-left"
                                onClick={() => {
                                    onShowChatList()
                                }}
                            />
                        </FlexBox>
                    </ConversationHeader.Actions>
                )}
            </ConversationHeader>
            <MessageList
                loading={false}
                loadingMore={false}
                loadingMorePosition="top"
                autoScrollToBottomOnMount
                autoScrollToBottom
                onYReachStart={!messages?.last ? handleFetchMore : () => {}}>
                {/* {messageGroupByDate &&
                    messageGroupByDate.length > 0 &&
                    messageGroupByDate.map((item, idx) =>
                        item.messageType === messageType.separator ? (
                            <MessageSeparator
                                key={idx}
                                content={item.data}
                                as="h2"
                            />
                        ) : item.messageType === messageType.text ||
                          item.messageType === messageType.image ? (
                            <Message
                                key={idx}
                                model={{
                                    // message: item?.data,
                                    sentTime: '',
                                    sender: item.fullName,
                                    direction: item.userId !== user?.id ? 0 : 1,
                                    position: item.position,
                                }}>
                                {item.isUseAvatar ? (
                                    <Avatar
                                        src={
                                            item.avatar ||
                                            'https://inkythuatso.com/uploads/thumbnails/800/2023/03/7-anh-dai-dien-trang-inkythuatso-03-15-26-45.jpg'
                                        }
                                        size="sm"
                                        alt={item.fullName}
                                    />
                                ) : (
                                    item.userId !== user?.id && (
                                        <Avatar
                                            src={
                                                'https://inkythuatso.com/uploads/thumbnails/800/2023/03/7-anh-dai-dien-trang-inkythuatso-03-15-26-45.jpg'
                                            }
                                            size="sm"
                                            alt={item.fullName}
                                        />
                                    )
                                )}
                                <Message.CustomContent>
                                    {item.messageType === messageType.image ? (
                                        <div className="flex flex-wrap justify-content-center align-items-center gap-1 max-w-28rem">
                                            {item?.data.map(
                                                (childrenItem, idx) => (
                                                    <img
                                                        className="bg-white border-round-sm"
                                                        style={{
                                                            objectFit: 'cover',
                                                            cursor: 'pointer',
                                                            width: '9rem',
                                                            height: '7rem',
                                                        }}
                                                        key={idx}
                                                        src={childrenItem}
                                                        alt="Akane avatar"
                                                        onClick={() => {
                                                            setImages(
                                                                item?.data,
                                                            )
                                                            setActiveIndex(idx)
                                                            setVisibleLightBox(
                                                                true,
                                                            )
                                                        }}
                                                    />
                                                ),
                                            )}
                                        </div>
                                    ) : (
                                        <span
                                            className="h-full"
                                            dangerouslySetInnerHTML={{
                                                __html: item.data,
                                            }}></span>
                                    )}
                                    <div className="">
                                        {!!visibleLightBox && (
                                            <Lightbox
                                                mainSrc={images[activeIndex]}
                                                nextSrc={() =>
                                                    setActiveIndex + 1
                                                }
                                                prevSrc={
                                                    images[
                                                        (activeIndex +
                                                            images?.length -
                                                            1) %
                                                            images?.length
                                                    ]
                                                }
                                                onCloseRequest={() =>
                                                    setVisibleLightBox(false)
                                                }
                                                onMovePrevRequest={
                                                    onPrevLightBox
                                                }
                                                onMoveNextRequest={
                                                    onNextLightBox
                                                }
                                            />
                                        )}
                                    </div>
                                </Message.CustomContent>
                            </Message>
                        ) : (
                            item.messageType === messageType.name && (
                                <MessageSeparator
                                    model={{
                                        sentTime: '',
                                        sender: item.fullName,
                                        direction:
                                            item.userId !== user?.id ? 0 : 1,
                                    }}
                                    key={idx}>
                                    {item.fullName}
                                </MessageSeparator>
                            )
                        ),
                    )} */}
                {messageDataList.map((item, idx) =>
                    item.messageType === messageType.separator ? (
                        <MessageSeparator
                            key={idx}
                            content={item.data}
                            as="h2"
                        />
                    ) : (
                        <MessageGroup
                            key={idx}
                            className="mb-2"
                            direction={item.userId !== user?.id ? 0 : 1}
                            sender={item.fullName}>
                            {item.userId !== user?.id && (
                                <Avatar
                                    size="sm"
                                    src={
                                        item.avatar ||
                                        'https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_960_720.png'
                                    }
                                />
                            )}
                            {item.userId !== user?.id &&
                                roomData?.userIds.length > 2 && (
                                    <MessageGroup.Header className="mr-3">
                                        {item.fullName}
                                    </MessageGroup.Header>
                                )}

                            <MessageGroup.Messages
                                className={` ${
                                    item.userId === user?.id
                                        ? 'outgoing'
                                        : 'incoming'
                                }`}>
                                {item.messages.map((mes, mesIdx) =>
                                    mes.messageType === messageType.text ? (
                                        <Message
                                            key={mesIdx}
                                            className={`${
                                                item.messages.length > 1
                                                    ? mesIdx === 0
                                                        ? 'top'
                                                        : mesIdx ===
                                                          item.messages.length -
                                                              1
                                                        ? 'bottom'
                                                        : 'center'
                                                    : ''
                                            }`}
                                            model={{message: mes.data}}
                                        />
                                    ) : (
                                        <Message
                                            key={mesIdx}
                                            className={`${
                                                item.messages.length > 1
                                                    ? mesIdx === 0
                                                        ? 'top'
                                                        : mesIdx ===
                                                          item.messages.length -
                                                              1
                                                        ? 'bottom'
                                                        : 'center'
                                                    : ''
                                            }`}>
                                            {' '}
                                            <Message.CustomContent>
                                                <div className="flex flex-wrap justify-content-center align-items-center gap-1 max-w-28rem">
                                                    {isArrayAndNotEmpty(
                                                        mes.data,
                                                    ) &&
                                                        mes?.data.map(
                                                            (
                                                                childrenItem,
                                                                idx,
                                                            ) => (
                                                                <img
                                                                    className="bg-white border-round-sm"
                                                                    style={{
                                                                        objectFit:
                                                                            'cover',
                                                                        cursor: 'pointer',
                                                                        width: '9rem',
                                                                        height: '7rem',
                                                                    }}
                                                                    key={idx}
                                                                    src={
                                                                        childrenItem
                                                                    }
                                                                    alt="Akane avatar"
                                                                    onClick={() => {
                                                                        setImages(
                                                                            mes?.data,
                                                                        )
                                                                        setActiveIndex(
                                                                            idx,
                                                                        )
                                                                        setVisibleLightBox(
                                                                            true,
                                                                        )
                                                                    }}
                                                                />
                                                            ),
                                                        )}
                                                </div>
                                                <div className="">
                                                    {!!visibleLightBox && (
                                                        <Lightbox
                                                            mainSrc={
                                                                images[
                                                                    activeIndex
                                                                ]
                                                            }
                                                            nextSrc={() =>
                                                                setActiveIndex +
                                                                1
                                                            }
                                                            prevSrc={
                                                                images[
                                                                    (activeIndex +
                                                                        images?.length -
                                                                        1) %
                                                                        images?.length
                                                                ]
                                                            }
                                                            onCloseRequest={() =>
                                                                setVisibleLightBox(
                                                                    false,
                                                                )
                                                            }
                                                            onMovePrevRequest={
                                                                onPrevLightBox
                                                            }
                                                            onMoveNextRequest={
                                                                onNextLightBox
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            </Message.CustomContent>{' '}
                                        </Message>
                                    ),
                                )}
                            </MessageGroup.Messages>
                        </MessageGroup>
                    ),
                )}
            </MessageList>
            <InputToolbox className="col-12 flex ">
                <div className="flex flex-grow-0 justify-content-center align-items-center">
                    <Button
                        className=" p-0 m-0 bg-white  border-2 hover:bg-blue-50 w-2rem h-2rem"
                        icon="pi pi-image"
                        style={{color: 'darkcyan'}}
                        onClick={() => setVisible(true)}
                    />{' '}
                    <Dialog
                        id="chat-uploader-dialog"
                        className=" col-12 md:col-6 m-0 p-0"
                        header={translateCommons('Upload image')}
                        visible={visible}
                        onHide={() => setVisible(false)}>
                        <ImageUploader
                            ref={el => (imageRef.current = el)}
                            callback={(e, value) => {
                                imageRef.current = value
                            }}
                            multiple={true}
                            extraData={true}
                        />
                        <Button
                            className="col-12  p-button-lg font-bold mr-2"
                            icon="pi pi-check-circle"
                            label={translateCommons('DONE UPLOAD')}
                            onClick={() => onSubmitImg()}
                        />
                    </Dialog>
                </div>

                <MessageInput
                    className="flex-grow-1 border-top-none"
                    value={message}
                    placeholder={translateCommons('Type message here')}
                    onChange={value => setMessage(value)}
                    attachButton={false}
                    sendButton={false}
                    onSend={() => handleSendChatMessage(messageType.text)}
                />
                <SendButton
                    className="flex-grow-0"
                    disabled={!message ?? false}
                    style={{color: 'darkcyan'}}
                    onClick={() => handleSendChatMessage(messageType.text)}
                />
            </InputToolbox>
        </ChatContainer>
    )
}

export default Chat
